<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {ref} from "vue";
import {CapacityFramework, CapacityFrameworkRole} from "@/types";
import IconTooltip from "@/components/tooltips/IconTooltip.vue";
import TitleAvatar from "@/components/avatars/TitleAvatar.vue";
import NavigationTab from "@/components/tabs/NavigationTab.vue";
import PublishedChip from "@/components/chips/PublishedChip.vue";
import DraftChip from "@/components/chips/DraftChip.vue";
import {useDate} from "@/composables/date";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import LastUpdateTooltip from "@/components/tooltips/LastUpdateTooltip.vue";
import AddMaintainerDialog from "@/Pages/capacity_frameworks/components/AddMaintainerDialog.vue";
import {useCapacityFramework} from "@/composables/capacity_framework";
import CapacityFrameworkActionsBtn from "@/Pages/capacity_frameworks/components/CapacityFrameworkActionsBtn.vue";
import {useAuth} from "@/composables/auth";
import {useColors} from "@/composables/colors";
import NavigationTabWithChip from "@/components/tabs/NavigationTabWithChip.vue";
import {useTeam} from "@/composables/team";

defineOptions({layout: BaseLayout})

const {user} = useAuth()
const {isGuest} = useTeam()
const {lastUsed} = useDate()
const {isAdmin, isAdminOrEditor, isGuest: guest} = useCapacityFramework()
const {randomColor} = useColors()

interface Props {
    title: string
    capacityFramework: CapacityFramework
    category: 'overview' | 'users' | 'assessments' | 'progression' | 'public' | 'versions'
    hasJoined: boolean
    role: CapacityFrameworkRole | null
}

const props = defineProps<Props>();

const tab = ref<'overview' | 'users' | 'assessments' | 'progression' | 'public' | 'versions'>(props.category);

const isAddMaintainerDialogOpen = ref<boolean>(false);

const goToOverview = () => {
    router.get(route('capacity-frameworks.show', {capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id}));
}

const goToProgression = () => {
    router.get(route('capacity-frameworks.progression.index', {capacityFramework: props.capacityFramework.id}));
}

const goToVersions = () => {
    router.get(route('capacity-frameworks.versions.index', {capacityFramework: props.capacityFramework.id}));
}

const goToUsers = () => {
    router.get(route('capacity-frameworks.users.index', {capacityFramework: props.capacityFramework.id}));
}

const likeCapacityFramework = () => {
    router.post(route('capacity-frameworks.like', {capacityFramework: props.capacityFramework.id}), {}, {
        preserveScroll: true,
    });
}

const editCapacityFramework = () => {
    router.get(route('capacity-frameworks.edit', {capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id}));
}

const editCapacityFrameworkForm = () => {
    router.get(route('capacity-frameworks.form', {capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id}));
}

const deleteCapacityFramework = () => {
    router.delete(route('capacity-frameworks.destroy', {capacityFramework: props.capacityFramework.id}));
}

const leaveCapacityFramework = () => {
    router.delete(route('capacity-frameworks.users.destroy', {
        capacityFramework: props.capacityFramework.id,
        user: user.value.id
    }));
}

const goToAssessments = (category: 'own' | 'public') => {
    router.get(route('capacity-frameworks.assessments.index', {
        capacityFramework: props.capacityFramework.id,
        filter: category
    }));
}
</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between">
            <v-col
                cols="12"
                :md="guest ? 12 : 7"
            >
                <h1 class="text-h4 font-weight-bold">
                    <title-avatar
                        :id="props.capacityFramework.id"
                        :title="props.capacityFramework.name"
                        size="48"
                    />
                    {{ props.title }}
                    <v-btn
                        variant="text"
                        color="black"
                        border="sm"
                        size="small"
                        @click="likeCapacityFramework"
                    >
                        👍&nbsp;<span class="font-weight-regular">
                            {{ props.capacityFramework.likes_count }}
                        </span>
                    </v-btn>
                </h1>
            </v-col>
            <v-col
                v-if="!guest"
                cols="12"
                md="5"
                align-self="start"
                class="text-end"
            >
                <div class="text-end">
                    <v-btn
                        v-if="isAdmin"
                        class="mr-2"
                        @click="isAddMaintainerDialogOpen = true"
                    >
                        Invite maintainers
                    </v-btn>
                    <v-btn
                        v-if="isAdminOrEditor"
                        color="black"
                        variant="text"
                        border="sm"
                        @click="editCapacityFrameworkForm"
                    >
                        <span class="font-weight-regular">
                            Edit template
                        </span>
                    </v-btn>
                    <capacity-framework-actions-btn
                        :capacity-framework="props.capacityFramework"
                        :hidden="!props.hasJoined"
                        :hide-editor-actions="!isAdminOrEditor"
                        :hide-admin-actions="!isAdmin"
                        @update="editCapacityFramework"
                        @delete="deleteCapacityFramework"
                        @leave="leaveCapacityFramework"
                    />
                </div>
            </v-col>
            <v-col
                cols="12"
                class="pa-0 text-body-2 text-medium-emphasis"
            >
                <draft-chip v-if="props.capacityFramework.status === 'draft'" />
                <published-chip v-if="props.capacityFramework.status === 'published'" />
                <span class="font-weight-bold text-black">Current version (v{{
                    props.capacityFramework.version_id
                }})</span> was updated
                <last-update-tooltip :datetime="props.capacityFramework.updated_at">
                    {{ lastUsed(props.capacityFramework.updated_at) }}
                </last-update-tooltip>
            </v-col>
            <v-col
                cols="12"
                class="px-0 pt-2 pb-0"
            >
                <v-chip
                    v-for="topic in props.capacityFramework.topics"
                    :key="topic.id"
                    variant="flat"
                    class="mx-1 mb-1 text-chip"
                    size="x-small"
                    :color="randomColor(topic.id)"
                >
                    {{ topic.name }}
                </v-chip>
            </v-col>
        </v-row>
        <v-row class="mt-5">
            <v-col
                cols="12"
                class="pa-0"
            >
                <v-tabs
                    v-model="tab"
                    align-tabs="start"
                    color="primary"
                >
                    <navigation-tab
                        :tab="tab"
                        value="overview"
                        @click="goToOverview"
                    >
                        Overview
                    </navigation-tab>
                    <navigation-tab
                        v-if="!isGuest"
                        :tab="tab"
                        value="assessments"
                        @click="goToAssessments('own')"
                    >
                        Your assessments
                    </navigation-tab>
                    <navigation-tab
                        v-if="!isGuest"
                        :tab="tab"
                        value="progression"
                        @click="goToProgression"
                    >
                        Your progression
                    </navigation-tab>
                    <navigation-tab
                        :tab="tab"
                        value="public"
                        @click="goToAssessments('public')"
                    >
                        Public assessments
                    </navigation-tab>
                    <navigation-tab-with-chip
                        :tab="tab"
                        :count="props.capacityFramework.users_count"
                        value="users"
                        @click="goToUsers"
                    >
                        Maintainers
                    </navigation-tab-with-chip>
                    <navigation-tab-with-chip
                        :tab="tab"
                        :count="props.capacityFramework.versions_count"
                        value="versions"
                        @click="goToVersions"
                    >
                        Versions
                    </navigation-tab-with-chip>
                </v-tabs>
            </v-col>
        </v-row>

        <slot />
    </v-container>
    <add-maintainer-dialog
        v-if="isAdmin"
        v-model="isAddMaintainerDialogOpen"
        :capacity-framework-id="props.capacityFramework.id"
    />
</template>

<style scoped>
.text-chip {
    font-size: 0.75rem;
}
</style>
